import { $isListNode } from '@lexical/list';
import { $convertFromMarkdownString, TextMatchTransformer, Transformer } from '@lexical/markdown';
import { $isTableNode } from '@lexical/table';
import { $isElementNode, $isTextNode, $getRoot, $isParagraphNode, LexicalNode } from 'lexical';
import {
  $isSuggestionMarkNode,
  $sourceSuggestionMark,
  $tooltipSuggestionMark,
  SuggestionMarkNode,
} from '../../nodes/SuggestionMarkNode';
import {
  $createSuggestionNode,
  $getSuggestionState,
  $isSuggestionNode,
} from '../../nodes/SuggestionNode';
import { Source } from '../../types';
import { isValidSource, parseAttributes } from './suggestion';

function textContainsBlockElements(text: string): boolean {
  return /<table|<ul|<ol|<div|<blockquote/i.test(text);
}

function normalizeLineBreaks(text: string): string {
  return text.replace(/<br\s*\/?>/gi, '\n');
}

function shouldBeBlockSuggestion(nodes: LexicalNode[]): boolean {
  if (nodes.length === 1 && $isParagraphNode(nodes[0])) {
    return false;
  }

  for (const node of nodes) {
    if ($isTableNode(node) || $isListNode(node)) {
      return true;
    }
    if ($isElementNode(node) && !node.isInline()) {
      return true;
    }
  }

  return false;
}

export const createInlineSuggestionTransformer = (
  transformers: Transformer[],
): TextMatchTransformer => ({
  type: 'text-match',
  dependencies: [],

  regExp: /<suggestion([^>]*)>(.*?)<\/suggestion>/,
  importRegExp: /<suggestion([^>]*)>(.*?)<\/suggestion>/,
  export: (node: LexicalNode, traverseChildren) => {
    if (!$isSuggestionNode(node)) {
      return null;
    }

    const state = $getSuggestionState(node);
    const attrs: string[] = [];
    if (state.source !== 'default') attrs.push(`source="${state.source}"`);
    if (state.tooltip) attrs.push(`tooltip="${state.tooltip}"`);
    const attrStr = attrs.length > 0 ? ` ${attrs.join(' ')}` : '';

    const output: string[] = [];
    output.push(`<suggestion${attrStr}>`);
    output.push(traverseChildren(node));
    output.push('</suggestion>');
    return output.join('');
  },

  replace: (node, match) => {
    const attrs = parseAttributes(match[1] || '');
    const rawContent = match[2];
    const source: Source = isValidSource(attrs.source) ? attrs.source : 'default';
    const tooltip = attrs.tooltip || null;

    // <br/> tags inside markdown tables break rendering
    const textContent = normalizeLineBreaks(rawContent);

    // Append to root — tempContainer is removed after parsing, so placement
    // doesn't affect the final tree. Using $insertNodeToNearestRoot crashes
    // when multiple <suggestion> tags exist because the first replacement
    // modifies/splits the text node, leaving stale offsets for later matches.
    const tempContainer = $createSuggestionNode(source, true, tooltip);
    $getRoot().append(tempContainer);
    $convertFromMarkdownString(textContent, transformers, tempContainer, true);

    const parsedChildren = tempContainer.getChildren();

    if (parsedChildren.length === 0) {
      tempContainer.remove();
      node.remove();
      return;
    }

    const hasBlockContent =
      textContainsBlockElements(textContent) || shouldBeBlockSuggestion(parsedChildren);
    const suggestion = $createSuggestionNode(source, hasBlockContent, tooltip);

    if (hasBlockContent) {
      parsedChildren.forEach(child => suggestion.append(child));
      tempContainer.remove();

      const nodeParent = node.getParent();
      if ($isParagraphNode(nodeParent)) {
        nodeParent.insertAfter(suggestion);
        node.remove();
        if (nodeParent.isEmpty()) nodeParent.remove();
      } else {
        node.replace(suggestion);
      }
    } else {
      if (parsedChildren.length === 1 && $isParagraphNode(parsedChildren[0])) {
        parsedChildren[0].getChildren().forEach(child => suggestion.append(child));
      } else {
        parsedChildren.forEach(child => suggestion.append(child));
      }

      tempContainer.remove();
      node.replace(suggestion);
    }

    suggestion.selectNext();
  },
});

export const createSuggestionMarkExportTransformer = (): TextMatchTransformer => ({
  type: 'text-match',
  dependencies: [SuggestionMarkNode],

  export: (node, _traverseChildren, traverseFormatting) => {
    if (!$isSuggestionMarkNode(node)) {
      return null;
    }

    const source = $sourceSuggestionMark(node);
    const tooltip = $tooltipSuggestionMark(node);
    const attrs: string[] = [];
    if (source !== 'default') attrs.push(`source="${source}"`);
    if (tooltip) attrs.push(`tooltip="${tooltip}"`);
    const attrStr = attrs.length > 0 ? ` ${attrs.join(' ')}` : '';

    const inner = node
      .getChildren()
      .map(child => {
        if ($isTextNode(child)) {
          return traverseFormatting
            ? traverseFormatting(child, child.getTextContent())
            : child.getTextContent();
        }
        return child.getTextContent();
      })
      .join('');

    return `<suggestion${attrStr}>${inner}</suggestion>`;
  },

  importRegExp: /(?!)/,
  regExp: /(?!)/,
  replace: () => {},
});
